import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Loader2, Wallet } from 'lucide-react'
import { Link } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

const GATE_Z = 2147483646

/**
 * Blocks Checkout until the cash day (shift) is open.
 *
 * @param {object} props
 * @param {boolean} props.busy
 * @param {(openingFloat: number) => void | Promise<void>} props.onOpenDay
 * @param {string} [props.error]
 */
export function DayOpenGate({ busy = false, onOpenDay, error }) {
  const rootRef = useRef(null)
  const [floatValue, setFloatValue] = useState('0')

  useEffect(() => {
    const enforce = () => {
      const el = rootRef.current
      if (!el || !el.isConnected) return
      el.style.setProperty('display', 'flex', 'important')
      el.style.setProperty('visibility', 'visible', 'important')
      el.style.setProperty('opacity', '1', 'important')
      el.style.setProperty('pointer-events', 'auto', 'important')
      el.style.setProperty('position', 'fixed', 'important')
      el.style.setProperty('inset', '0', 'important')
      el.style.setProperty('z-index', String(GATE_Z), 'important')
    }
    enforce()
    const tick = window.setInterval(enforce, 500)
    return () => window.clearInterval(tick)
  }, [])

  if (typeof document === 'undefined') return null

  return createPortal(
    <div
      ref={rootRef}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="day-open-gate-title"
      className="fixed inset-0 z-[2147483646] flex items-center justify-center bg-slate-950/90 p-4"
    >
      <div className="w-full max-w-md rounded-2xl border border-[#E5DED3] bg-[#FBFAF7] p-6 shadow-2xl shadow-[#8A6D42]/20">
        <div className="mb-3 flex items-center gap-2 text-slate-900">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-[#C9A66B] to-[#B08D57] text-white">
            <Wallet className="h-5 w-5" />
          </div>
          <h2 id="day-open-gate-title" className="text-xl font-semibold">
            Open the day first
          </h2>
        </div>
        <p className="mb-4 text-sm text-slate-500">
          Enter the cash in the drawer, then open the day. You cannot take payment until the day is open.
        </p>
        <label className="mb-1 block text-sm font-medium" htmlFor="day-open-float">
          Opening cash (AED)
        </label>
        <input
          id="day-open-float"
          type="number"
          min="0"
          step="0.01"
          value={floatValue}
          onChange={(e) => setFloatValue(e.target.value)}
          className={cn(
            'mb-3 h-11 w-full rounded-xl border border-[#E5DED3] bg-white px-3 text-base',
            'focus:outline-none focus:ring-2 focus:ring-[#B08D57]/40',
          )}
          disabled={busy}
        />
        {error ? <p className="mb-3 text-sm text-red-600">{error}</p> : null}
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            type="button"
            className="h-11 flex-1"
            disabled={busy}
            onClick={() => onOpenDay(Number(floatValue) || 0)}
          >
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Open day
          </Button>
          <Button type="button" variant="outline" className="h-11 flex-1" asChild>
            <Link to="/">Leave POS</Link>
          </Button>
        </div>
        <p className="mt-3 text-xs text-slate-500">
          Full cash day (expenses, day end, Z) is under{' '}
          <Link className="font-medium text-[#B08D57] underline" to="/cash">
            Cash day
          </Link>
          .
        </p>
      </div>
    </div>,
    document.body,
  )
}
